import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Alert, Button, Dropdown, Menu, Modal, Progress, Upload } from 'antd'
import { connect } from 'react-redux'
import { getActiveLanguage, getTranslate } from 'react-localize-redux'
import { faDownload, faFileImport } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getUser } from '../../../reducers/UserReducer'
import { getLanguageByLocale } from '../../../utils'
import { callFunctionIfDefined } from '../../../utils/functionHelper'
import { onError, onSuccess, onWarning } from '../../../utils/apiHelper'
import { getInternshipsImportTemplate, importInternships, InternshipImportModes } from '../../../utils/api/internship'
import TooltipIconButton from '../../antd/Buttons/TooltipIconButton'
import useFakeProgress from '../../../hooks/useFakeProgress'
import useScreenSize from '../../../hooks/useScreen'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { ColorsPalette } from '../../../config/colors'
import { FileTypes } from '../../../utils/constants'
import { isFileTooLarge } from '../../../utils/files'
import FileTooLargeMessage from '../Messages/FileTooLargeMessage'
import InternshipsImportReport, { translateCode } from './InternshipsImportReport'

import '../../../assets/students-import-modal.scss'

const { Dragger } = Upload

const UPLOAD_WIDTH = 860
const REPORT_WIDTH = 1300
const REPORT_TOP = 24
const PROGRESS_INTERVAL = 400
const ACCEPTED_TYPES = Object.freeze(['.xlsx', FileTypes.EXCELX])

const Steps = Object.freeze({
  UPLOAD: 'UPLOAD',
  PREVIEW: 'PREVIEW',
  DONE: 'DONE'
})

const mapStateToProps = state => ({
  t: getTranslate(state.locale),
  activeLanguage: getActiveLanguage(state.locale).code,
  user: getUser(state.getUser)
})

const ImportInternshipsButton = ({ activeLanguage, school, user, t, afterUpload }) => {
  const { isDraft } = useContext(GlobalContext)
  const screen = useScreenSize()
  const { percentage, start: startProgress, complete: completeProgress, reset: resetProgress } = useFakeProgress(PROGRESS_INTERVAL)

  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState(Steps.UPLOAD)
  const [fileList, setFileList] = useState([])
  const [tooLargeFile, setTooLargeFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState(null)

  const unmounted = useRef(false)

  useEffect(() => () => { unmounted.current = true }, [])

  const reset = useCallback(() => {
    resetProgress()
    setStep(Steps.UPLOAD)
    setFileList([])
    setTooLargeFile(null)
    setBusy(false)
    setReport(null)
  }, [resetProgress])

  const close = useCallback(() => {
    if (busy) {
      return
    }

    reset()
    setVisible(false)
  }, [busy, reset])

  const announceImport = useCallback(summary => {
    if (summary.created > 0) {
      callFunctionIfDefined(afterUpload)
    }

    if (summary.errors === 0) {
      return onSuccess(t('internships_import.import.success'))
    }

    return summary.created > 0
      ? onWarning(t('internships_import.import.partial'))
      : onError(t('internships_import.import.failure'))
  }, [afterUpload, t])

  const send = useCallback(mode => {
    if (fileList.length === 0) {
      return
    }

    setBusy(true)
    startProgress()

    importInternships(user, fileList[0], { school, isDraft, mode }).then(received => {
      if (unmounted.current) {
        return
      }

      completeProgress()
      setReport(received)
      setStep(mode === InternshipImportModes.IMPORT ? Steps.DONE : Steps.PREVIEW)
      setBusy(false)

      if (mode === InternshipImportModes.IMPORT) {
        announceImport(received.summary)
      }
    }, error => {
      if (unmounted.current) {
        return
      }

      resetProgress()
      setBusy(false)

      onError(error && error.message ? translateCode(error.message, t) : t('internships_import.errors.UNEXPECTED'))
    })
  }, [announceImport, completeProgress, fileList, isDraft, resetProgress, school, startProgress, t, user])

  const downloadTemplate = useCallback(() => {
    getInternshipsImportTemplate(user, { language: getLanguageByLocale(activeLanguage), school }).then(() => {
      onSuccess(t('import_internships_button.download_template.success'))
    }).catch(() => {
      onError(t('import_internships_button.download_template.error'))
    })
  }, [activeLanguage, school, t, user])

  const showingReport = step !== Steps.UPLOAD && report !== null

  const modalWidth = useMemo(
    () => showingReport ? Math.min(REPORT_WIDTH, Math.round(screen.width * 0.9)) : UPLOAD_WIDTH,
    [screen.width, showingReport]
  )

  const validRows = report === null ? 0 : report.summary.created

  const menu = (
    <Menu>
      <Menu.Item onClick={() => { reset(); setVisible(true) }}>
        <FontAwesomeIcon icon={faFileImport} />&nbsp;
        {t('import_internships_button.tooltip_title')}
      </Menu.Item>
      <Menu.Item onClick={downloadTemplate}>
        <FontAwesomeIcon icon={faDownload} />&nbsp;
        {t('import_internships_button.download_template')}
      </Menu.Item>
    </Menu>
  )

  const footer = useMemo(() => {
    if (step === Steps.DONE) {
      return [
        <Button key='close' type='primary' onClick={close}>{t('Close')}</Button>
      ]
    }

    if (step === Steps.PREVIEW) {
      return [
        <Button key='back' disabled={busy} onClick={() => setStep(Steps.UPLOAD)}>{t('Back')}</Button>,
        <Button
          key='confirm'
          type='primary'
          loading={busy}
          disabled={busy || validRows === 0}
          onClick={() => send(InternshipImportModes.IMPORT)}
        >
          {t('internships_import.confirm', { count: validRows })}
        </Button>
      ]
    }

    return [
      <Button key='cancel' disabled={busy} onClick={close}>{t('Cancel')}</Button>,
      <Button
        key='analyse'
        type='primary'
        loading={busy}
        disabled={busy || fileList.length === 0}
        onClick={() => send(InternshipImportModes.DRY_RUN)}
      >
        {t('internships_import.analyse')}
      </Button>
    ]
  }, [busy, close, fileList.length, send, step, t, validRows])

  return (
    <>
      <Dropdown overlay={menu} trigger={['click']}>
        <TooltipIconButton
          title={t('import_internships_button.tooltip_title')}
          icon={faFileImport}
        />
      </Dropdown>
      <Modal
        title={t('import_internships_button.modal_title')}
        visible={visible}
        width={modalWidth}
        style={showingReport ? { top: REPORT_TOP } : undefined}
        footer={footer}
        maskClosable={!busy}
        closable={!busy}
        onCancel={close}
        destroyOnClose
      >
        <div className='students-import-modal-body import-modal-body'>
          {isDraft && (
            <Alert
              type='warning'
              message={<span style={{ color: ColorsPalette.GREY }}>{t('import_internships_button.warn.is_draft')}</span>}
              showIcon
              style={{ marginBottom: '10px' }}
            />
          )}
          {busy && (
            <div className='importing-animation'>
              <Progress type='circle' percent={percentage} />
              <p className='importing-label'>
                {step === Steps.PREVIEW ? t('internships_import.importing') : t('internships_import.analysing')}
              </p>
            </div>
          )}
          {!busy && step === Steps.UPLOAD && (
            <Dragger
              fileList={fileList}
              name='file'
              accept={ACCEPTED_TYPES.join(',')}
              beforeUpload={file => {
                if (!ACCEPTED_TYPES.includes(file.type)) {
                  onError(t('internships_import.errors.UNSUPPORTED_FILE_TYPE'))

                  return false
                }

                if (isFileTooLarge(file)) {
                  setTooLargeFile(file)
                  setFileList([])

                  return false
                }

                setTooLargeFile(null)
                setFileList([file])

                return false
              }}
              onRemove={() => setFileList([])}
            >
              <p className='ant-upload-text'>{t('import_internships_button.upload_text')}</p>
              <p className='ant-upload-hint'>{t('internships_import.upload_hint')}</p>
            </Dragger>
          )}
          {!busy && step === Steps.UPLOAD && <FileTooLargeMessage file={tooLargeFile} />}
          {!busy && showingReport && (
            <InternshipsImportReport report={report} height={screen.height} t={t} />
          )}
        </div>
      </Modal>
    </>
  )
}

export default connect(mapStateToProps)(ImportInternshipsButton)
